import style from "@/styles/pages/PurchasePrice/PurchasePrice.module.css";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "@/store/dtm";
import CurrencyInput from "react-currency-input-field";
import { setPropertyValue } from "@/slices/propertyInfoSlice";
import SlideTransition from "@/styles/animation/SlideTransition";
import { setApplicationProgress } from "@/slices/applicationSlice";
import { ApplicationDto } from "@/lib/DTMWebAPIClient.generated";
import { createApiClient } from "@/lib/Requests";
import { setClickInsightApplicationProgress, setClickInsightFormErrors } from "@/lib/util";

export default function PropertyValue() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [price, setPrice] = useState("");

    const propertyInfo = useSelector(
        (state: RootState) => state.propertyInfo.propertyInfo
    );
    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const homeAddress = useSelector(
        (state: RootState) => state.homeAddress.address
    );
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

    const methods = useForm({
        defaultValues: {
            propertyValue: ""
        },
    });

    useEffect(() => {
        document.title = "Enter Current Value";
        methods.setValue("propertyValue", propertyInfo.propertyValue == 0 ? "" : propertyInfo.propertyValue.toString());
        setPrice(propertyInfo.propertyValue == 0 ? "" : propertyInfo.propertyValue.toString());

        client.apiApplicationsFetch().then((result) => {
            result.application = application;
            result.propertyInfo = propertyInfo;
            result.homeAddress = homeAddress;
            client.apiApplicationsSave(new ApplicationDto({
                applicationData: JSON.stringify(result)
            })).catch((error) => {
                console.log("Error saving application info");
            });
        }).catch((error) => {
            console.log("Error fetching application info");
        });

        dispatch(setApplicationProgress(1));
    }, []);

    const onError = (errors: object) => {
        setClickInsightFormErrors(errors);
    }

    const onSubmit = (data: any) => {
        dispatch(setPropertyValue(+price));
        setClickInsightApplicationProgress('What is the current value of your property?');
        navigate("/single/mortgage-balance");
    };

    return (
        <>
            <SlideTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Address Header"
                    >
                        <h1 aria-label="Address Header">What is the current value of your property?</h1>
                        <p className="sub-heading">Provide a reasonable estimate or enter the purchase price of your home.</p>
                        <div className={"flex flex-row mt-5"}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                >
                                    <div className="flex md:flex-row flex-col w-max">
                                        <div className="mt-5">
                                            <label htmlFor="propertyValue">Estimated Property Value</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.propertyValue?.message
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[195px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <CurrencyInput
                                                    id="propertyValue"
                                                    placeholder="$"
                                                    prefix={"$"}
                                                    decimalsLimit={2}
                                                    decimalScale={2}
                                                    allowNegativeValue={false}
                                                    value={price}
                                                    maxLength={10}
                                                    className={clsx(
                                                        methods.formState?.errors?.propertyValue?.message ? "error" : "",
                                                        "mt-2 w:[220px] md:w-[222px]"
                                                    )}
                                                    {...methods.register("propertyValue", {
                                                        required: "Enter the current value of your property.",
                                                        validate: {
                                                            propertyValue: (c) => {
                                                                let currentAmount = c.replaceAll("$", "").replaceAll(",", "");
                                                                if (+currentAmount > 0) {
                                                                    return true
                                                                } else {
                                                                    return "Current value cannot be zero."
                                                                }
                                                            },
                                                        },
                                                    })}
                                                    onValueChange={(value, name, values) => value != undefined ? setPrice(value) : setPrice("")}
                                                />
                                            </div>
                                            {methods.formState?.errors?.propertyValue?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState?.errors?.propertyValue?.message.toString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex max-sm:hidden">
                                        <div className="mb-5 mr-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-169 sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                            >
                                                Continue
                                            </button>
                                        </div>
                                    </div>
                                    <div className="sm:hidden">
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                            >
                                                Continue
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-full sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </SlideTransition>
        </>
    );
}